// frontend/src/pages/Rosters/RosterDashboard.js
import React, { useCallback, useEffect, useState } from "react";
import { api } from "../../api/client";
import { Link, useNavigate } from "react-router-dom";

export default function RosterDashboard() {
  const [rosters, setRosters] = useState([]);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState("");
  const [hoverId, setHoverId] = useState(null);
  const navigate = useNavigate();

  const fetchFirst = useCallback(async (endpoints) => {
    for (const path of endpoints) {
      try {
        const res = await api.get(path);
        if (res && res.status >= 200 && res.status < 300) {
          const arr = Array.isArray(res.data) ? res.data : res.data?.results ?? [];
          if (Array.isArray(arr)) return arr;
        }
      } catch {}
    }
    throw new Error("No roster endpoint responded successfully.");
  }, []);

  const load = useCallback(async () => {
    setBusy(true);
    setError("");
    try {
      const endpoints = [
        "/api/educators/me/groups",
        "/api/educator/groups",
        "/api/me/groups",
        "/api/rosters",
        "/api/groups",
      ];
      const arr = await fetchFirst(endpoints);
      setRosters(arr);
    } catch (e) {
      setError("Unable to load rosters.");
    } finally {
      setBusy(false);
    }
  }, [fetchFirst]);

  useEffect(() => {
    load();
  }, [load]);

  const getId = (r) =>
    r?.id ??
    r?.roster_id ??
    r?.uuid ??
    r?.pk ??
    r?.group_id ??
    (r?.group ? r.group.id : undefined);

  const goToRoster = (rid) => {
    if (rid == null) {
      console.warn("Roster missing id; cannot open.", rid);
      return;
    }
    navigate(`/rosters/${rid}`);
  };

  const onCardKeyDown = (e, rid) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      goToRoster(rid);
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.headerRow}>
        <h1 style={{ margin: 0 }}>Rosters</h1>
        <Link to="/rosters/new" style={styles.primaryBtn}>New Roster</Link>
      </div>

      {busy && <div>Loading…</div>}
      {error && <div style={styles.error}>{error}</div>}

      {!busy && !error && rosters.length === 0 && (
        <div style={styles.empty}>
          <p>No rosters yet.</p>
          <Link to="/rosters/new" style={styles.primaryBtn}>Create your first roster</Link>
        </div>
      )}

      <div style={styles.grid}>
        {rosters.map((r) => {
          const rid = getId(r);
          const title =
            r?.name != null
              ? r.name
              : (r?.title != null ? r.title : (rid != null ? `Roster ${rid}` : "Roster"));
          const desc = r?.description;
          const isHover = String(hoverId) === String(rid);

          return (
            <div
              key={rid ?? title}
              role="button"
              tabIndex={0}
              onClick={() => goToRoster(rid)}
              onKeyDown={(e) => onCardKeyDown(e, rid)}
              onMouseEnter={() => setHoverId(rid)}
              onMouseLeave={() => setHoverId(null)}
              onFocus={() => setHoverId(rid)}
              onBlur={() => setHoverId(null)}
              style={{
                ...styles.card,
                ...(isHover ? styles.cardHover : null),
              }}
              aria-label={`Open roster ${title}`}
            >
              <div style={styles.cardHeader}>
                <div>
                  <div
                    style={{
                      ...styles.cardTitle,
                      textDecoration: isHover ? "underline" : "none",
                    }}
                  >
                    {title}
                  </div>
                  {!!desc && <div style={styles.cardSub}>{desc}</div>}
                </div>
                <div style={styles.chevron} aria-hidden="true">›</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const styles = {
  page: { maxWidth: 1000, margin: "0 auto", padding: "16px" },
  headerRow: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
    gap: 12,
  },
  card: {
    border: "1px solid #E5E7EB",
    borderRadius: 10,
    padding: 16,
    background: "#89c3e8", // brand light blue
    cursor: "pointer",
    transition: "all 120ms ease",
    outline: "none",
    color: "#042d60", // dark brand blue for text
  },
  cardHover: {
    background: "#5789be", // darker blue on hover
    boxShadow: "0 6px 14px rgba(0,0,0,0.1)",
    transform: "translateY(-2px)",
    color: "#fff", // white text on hover
  },
  cardHeader: { display: "flex", alignItems: "center", justifyContent: "space-between" },
  cardTitle: { fontWeight: 600, fontSize: 18 },
  cardSub: { marginTop: 4, fontSize: 14 },
  chevron: {
    fontSize: 20,
    lineHeight: "20px",
    transition: "transform 120ms ease",
  },
  primaryBtn: {
    border: "1px solid #2563EB",
    borderRadius: 8,
    padding: "8px 12px",
    background: "#2563EB",
    color: "#fff",
    textDecoration: "none",
    cursor: "pointer",
  },
  empty: {
    border: "1px dashed #D1D5DB",
    borderRadius: 10,
    padding: 16,
    textAlign: "center",
    background: "#F9FAFB",
    marginTop: 12,
  },
  error: {
    color: "#b00020",
    background: "#FFEAEA",
    border: "1px solid #F5B5B5",
    borderRadius: 8,
    padding: "8px 10px",
  },
};
